import { useCallback, useMemo, useState } from "react";

import {
  CustomerModel,
  DashboardModel,
  type DashboardRowHeight,
  type DashboardType,
  EmbeddedDashboardTypes,
} from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { arrayMoveImmutable } from "array-move";
import findIndex from "lodash/findIndex";

import { getDashboardType } from "../Components/Dashboard/utils/widgetUtils";
import { GlobalDashboardId } from "../utils/common";
import { filterExcludeWidgetsIfStandaloneCustomer } from "../utils/widgets";
import { useAuthContext } from "./AuthContext";
import { useCustomerContext } from "./CustomerContext";
import { useInitGCPLensOrAWSLens } from "./Dashboards/InitGCPOrAWSLens";
import { useCachedHook } from "./DynamicContext/useCachedHook";
import { type DashboardsContextType, useDashboardsContext } from "./useDashboardsContext";

export type CurrentDashboardContextType = {
  dashboardType?: DashboardType | null;
  currentDashboard?: DashboardsContextType["dashboards"][number];
  widgets: NonNullable<DashboardsContextType["dashboards"][number]["widgets"]>;
  clearDashboardWidgets: () => Promise<void>;
  moveWidget: (activeName: string, overName: string) => Promise<void>;
  removeWidgetFromDashboard: (widgetId: string) => Promise<void>;
  addWidgetToDashboard: (widgetId: string) => Promise<void>;
  changeWidgetWidth: (widgetId: string, width: number) => Promise<void>;
  setCurrentDashboardByName: (name?: string) => void;
  isCustomDashboard: boolean;
  isEmbeddedDashboard: boolean;
  updateWidgets(widgets: Widget[]): Promise<void>;
  updateHeights(heights: DashboardRowHeight[]): Promise<void>;
};

export type Widget = DashboardsContextType["dashboards"][number]["widgets"][number];

const useNonCachedCurrentDashboardContext = () => {
  const { customer, isProductOnlyCustomer } = useCustomerContext();

  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const {
    dashboards,
    removeWidgetFromDashboard: removeWidgetFromDashboardById,
    addWidgetToDashboard: addWidgetToDashboardById,
    clearDashboardWidgets: clearDashboardWidgetsById,
  } = useDashboardsContext();

  const [currentDashboardId, setCurrentDashboardId] = useState<string | undefined>(undefined);

  const currentDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === currentDashboardId),
    [dashboards, currentDashboardId]
  );

  useInitGCPLensOrAWSLens(currentDashboard);

  const widgets = useMemo(
    () =>
      currentDashboard?.widgets?.filter((widget) =>
        filterExcludeWidgetsIfStandaloneCustomer(widget.name, isProductOnlyCustomer)
      ) ?? [],
    [currentDashboard?.widgets, isProductOnlyCustomer]
  );

  const dashboardId = useMemo(() => currentDashboard?.id, [currentDashboard?.id]);

  const newDashboardDocRef = useMemo(
    () =>
      dashboardId
        ? getCollection(DashboardModel)
            .doc("customization")
            .collection("users")
            .doc(currentUser.uid)
            .collection("duc")
            .doc(customer.id)
            .collection("dashboards")
            .doc(dashboardId)
        : undefined,
    [currentUser.uid, dashboardId, customer.id]
  );

  const publicDashboardsDocRef = useMemo(() => {
    if (!dashboardId) {
      return;
    }
    return getCollection(CustomerModel).doc(customer.id).collection("publicDashboards").doc(dashboardId);
  }, [dashboardId, customer.id]);

  const setCurrentDashboardByName = useCallback(
    (name?: string) => {
      if (name === currentDashboard?.name) {
        return;
      }

      const dashboard = dashboards.find((d) => d.name === name);
      setCurrentDashboardId(dashboard?.id);
    },
    [dashboards, currentDashboard?.name]
  );

  const moveWidget = useCallback(
    async (activeName: string, overName: string) => {
      const oldIndex = findIndex(widgets, { name: activeName });
      const newIndex = findIndex(widgets, { name: overName });
      const newWidgetsOrder = arrayMoveImmutable(widgets, oldIndex, newIndex);
      if (currentDashboard?.isPublic) {
        await publicDashboardsDocRef?.update({
          widgets: newWidgetsOrder,
        });

        return;
      }

      await newDashboardDocRef?.update({
        widgets: newWidgetsOrder,
      });
    },
    [currentDashboard, newDashboardDocRef, publicDashboardsDocRef, widgets]
  );

  const updateWidgets = useCallback(
    async (widgets: Widget[]) => {
      if (currentDashboard?.isPublic) {
        await publicDashboardsDocRef?.update({
          widgets,
        });
        return;
      }

      await newDashboardDocRef?.update({
        widgets,
      });
    },
    [currentDashboard, newDashboardDocRef, publicDashboardsDocRef]
  );

  const updateHeights = useCallback(
    async (heights: DashboardRowHeight[]) => {
      if (currentDashboard?.isPublic) {
        await publicDashboardsDocRef?.update({
          heights,
        });
        return;
      }

      await newDashboardDocRef?.update({
        heights,
      });
    },
    [currentDashboard, newDashboardDocRef, publicDashboardsDocRef]
  );

  const changeWidgetWidth = useCallback(
    async (widgetId: string, width: number) => {
      const updateWidgets = () =>
        widgets.map((widget: Widget) => ({
          ...widget,
          cardWidth: widgetId === widget.name ? width : widget.cardWidth,
        }));

      if (currentDashboard?.isPublic) {
        await publicDashboardsDocRef?.update({
          widgets: updateWidgets(),
        });

        return;
      }

      await newDashboardDocRef?.update({
        widgets: updateWidgets(),
      });
    },
    [currentDashboard?.isPublic, newDashboardDocRef, publicDashboardsDocRef, widgets]
  );

  const dashboardType = currentDashboard?.dashboardType;

  const isCustomDashboard = useMemo(
    () =>
      !!(
        currentDashboard &&
        currentDashboard.id !== GlobalDashboardId.Home &&
        getDashboardType(currentDashboard.name) === currentDashboard.name
      ),
    [currentDashboard]
  );

  const isEmbeddedDashboard = useMemo(
    () =>
      !!(
        currentDashboard &&
        currentDashboard.dashboardType &&
        EmbeddedDashboardTypes.includes(currentDashboard.dashboardType)
      ),
    [currentDashboard]
  );

  const removeWidgetFromDashboard = useCallback(
    async (name: string) => {
      if (!currentDashboard) {
        return;
      }
      await removeWidgetFromDashboardById(currentDashboard?.id, name);
    },
    [currentDashboard, removeWidgetFromDashboardById]
  );

  const addWidgetToDashboard = useCallback(
    async (name: string) => {
      if (!currentDashboard) {
        return;
      }
      await addWidgetToDashboardById(currentDashboard?.id, name);
    },
    [currentDashboard, addWidgetToDashboardById]
  );

  const clearDashboardWidgets = useCallback(async () => {
    if (!currentDashboard) {
      return;
    }
    await clearDashboardWidgetsById(currentDashboard?.id);
  }, [currentDashboard, clearDashboardWidgetsById]);

  return useMemo<CurrentDashboardContextType>(
    () => ({
      dashboardType,
      widgets,
      moveWidget,
      updateWidgets,
      updateHeights,
      currentDashboard,
      changeWidgetWidth,
      isCustomDashboard,
      isEmbeddedDashboard,
      removeWidgetFromDashboard,
      addWidgetToDashboard,
      setCurrentDashboardByName,
      clearDashboardWidgets,
    }),
    [
      addWidgetToDashboard,
      setCurrentDashboardByName,
      changeWidgetWidth,
      clearDashboardWidgets,
      currentDashboard,
      dashboardType,
      isCustomDashboard,
      isEmbeddedDashboard,
      moveWidget,
      updateWidgets,
      updateHeights,
      removeWidgetFromDashboard,
      widgets,
    ]
  );
};

export function useCurrentDashboardContext() {
  return useCachedHook(useNonCachedCurrentDashboardContext);
}
